class SoundNotificationService {
  private static instance: SoundNotificationService;
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, HTMLAudioElement> = new Map();
  private isEnabled: boolean = true;
  private basePath: string = '/';

  private constructor() {
    this.initializeAudioContext();
    this.preloadSounds();
  }

  static getInstance(): SoundNotificationService {
    if (!SoundNotificationService.instance) {
      SoundNotificationService.instance = new SoundNotificationService();
    }
    return SoundNotificationService.instance;
  }

  setBasePath(path: string): void {
    this.basePath = path.endsWith('/') ? path : `${path}/`;
    this.preloadSounds();
  }

  private initializeAudioContext(): void {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Audio context not supported:', error);
    }
  }

  private preloadSounds(): void {
    // Clear existing sounds
    this.sounds.clear();
    
    const soundFiles = {
      pendingChat: `${this.basePath}sounds/pending_notification.mp3`,
    };

    Object.entries(soundFiles).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      audio.volume = 0.7; // Set default volume
      audio.addEventListener('error', (e) => {
        console.warn(`Failed to load sound from ${path}:`, e);
      });
      this.sounds.set(key, audio);
    });
  }

  async playSound(soundKey: string): Promise<void> {
    if (!this.isEnabled) return;

    try {
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }

      const audio = this.sounds.get(soundKey);
      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;
        await audio.play();
      } else {
        console.warn(`Sound '${soundKey}' not found`);
      }
    } catch (error) {
      console.warn('Failed to play sound:', error);
    }
  }

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('soundNotificationsEnabled', enabled.toString());
  }

  isNotificationEnabled(): boolean {
    const stored = localStorage.getItem('soundNotificationsEnabled');
    return stored !== null ? stored === 'true' : true;
  }

  setVolume(soundKey: string, volume: number): void {
    const audio = this.sounds.get(soundKey);
    if (audio) {
      audio.volume = Math.max(0, Math.min(1, volume));
    }
  }

  // Request permission for audio playback (some browsers require user interaction)
  async requestAudioPermission(): Promise<boolean> {
    try {
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }
      return true;
    } catch (error) {
      console.warn('Audio permission denied:', error);
      return false;
    }
  }
}

export const soundNotificationService = SoundNotificationService.getInstance();
